import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, IconButton, Tooltip,
  CircularProgress, Typography, Box
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useFirebase } from '../../context/FirebaseContext';
import { getCopilotModels, createCopilotModel, updateCopilotModel, deleteCopilotModel } from '../../api/models';
import logger from '../../utils/logger';
import './AdminView.css';

const CopilotModelsTab = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const { auth } = useFirebase();

  const loadModels = async () => {
    try {
      setLoading(true);
      const response = await new Promise((resolve) => {
        getCopilotModels(auth, resolve);
      });
      if (Array.isArray(response)) {
        setModels(response);
      }
    } catch (error) {
      logger.error('Error loading models:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenDialog = (model = null) => {
    setSelectedModel(model || {
      modelId: '',
      modelName: '',
      modelDescription: '',
      modelEnabled: true,
      premium: false,
      systemUserIdentity: 'system',
      inputPricePer1m: 0,
      cachedInputPricePer1m: 0,
      outputPricePer1m: 0,
      settingsJson: JSON.stringify({
        tools_available: true,
        streaming_available: true,
        web_search_available: true
      }, null, 2)
    });
    setEditMode(!!model);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedModel(null);
  };

  const handleSaveModel = async () => {
    try {
      setLoading(true);
      const modelData = {
        ...selectedModel,
        settingsJson: typeof selectedModel.settingsJson === 'string'
          ? selectedModel.settingsJson
          : JSON.stringify(selectedModel.settingsJson)
      };

      if (editMode) {
        await new Promise((resolve) => {
          updateCopilotModel(auth, resolve, selectedModel.modelId, modelData);
        });
      } else {
        await new Promise((resolve) => {
          createCopilotModel(auth, modelData, resolve);
        });
      }
      await loadModels();
      handleCloseDialog();
    } catch (error) {
      logger.error('Error saving model:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteModel = async (modelId) => {
    if (window.confirm('Are you sure you want to delete this model?')) {
      try {
        setLoading(true);
        await new Promise((resolve) => {
          deleteCopilotModel(auth, modelId, resolve);
        });
        await loadModels();
      } catch (error) {
        logger.error('Error deleting model:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleModelChange = (field, value) => {
    setSelectedModel(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading && models.length === 0) {
    return (
      <Box className="admin-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="admin-tab-content">
      {/* Header */}
      <Box className="admin-section-header">
        <Typography variant="h6">AI Models</Typography>
        <Box className="admin-actions">
          <Button
            variant="outlined"
            onClick={loadModels}
            startIcon={<RefreshIcon />}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            onClick={() => handleOpenDialog()}
            startIcon={<AddIcon />}
            size="small"
          >
            Add Model
          </Button>
        </Box>
      </Box>

      {/* Desktop Table */}
      <Box className="admin-table-desktop">
        <TableContainer component={Paper} className="admin-table-container">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Model</TableCell>
                <TableCell className="admin-hide-tablet">Description</TableCell>
                <TableCell align="right">Input $/1M</TableCell>
                <TableCell align="right" className="admin-hide-mobile">Output $/1M</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell className="admin-hide-mobile">Premium</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {models.map((model) => (
                <TableRow key={model.modelId} className="admin-table-row">
                  <TableCell>
                    <Typography fontWeight="500">{model.modelName}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {model.modelId}
                    </Typography>
                  </TableCell>
                  <TableCell className="admin-hide-tablet admin-desc-cell">
                    {model.modelDescription?.substring(0, 60)}
                    {model.modelDescription?.length > 60 ? '...' : ''}
                  </TableCell>
                  <TableCell align="right">
                    ${model.inputPricePer1m?.toFixed(2) || '0.00'}
                  </TableCell>
                  <TableCell align="right" className="admin-hide-mobile">
                    ${model.outputPricePer1m?.toFixed(2) || '0.00'}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={model.modelEnabled}
                      size="small"
                      disabled
                    />
                  </TableCell>
                  <TableCell className="admin-hide-mobile">
                    <Switch
                      checked={model.premium}
                      size="small"
                      disabled
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleOpenDialog(model)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDeleteModel(model.modelId)} size="small" color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {models.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No models found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Mobile Cards */}
      <Box className="admin-cards-mobile">
        {models.length === 0 && (
          <Paper className="admin-empty-state">
            <Typography>No models found</Typography>
          </Paper>
        )}
        {models.map((model) => (
          <Paper key={model.modelId} className="admin-model-card">
            <Box className="admin-model-card-header">
              <Box>
                <Typography fontWeight="600">{model.modelName}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {model.modelId}
                </Typography>
              </Box>
              <Box className="admin-model-badges">
                {model.modelEnabled && (
                  <span className="admin-badge admin-badge-success">On</span>
                )}
                {model.premium && (
                  <span className="admin-badge admin-badge-premium">Pro</span>
                )}
              </Box>
            </Box>
            {model.modelDescription && (
              <Typography className="admin-model-desc" variant="body2">
                {model.modelDescription}
              </Typography>
            )}
            <Box className="admin-model-pricing">
              <span>In: ${model.inputPricePer1m?.toFixed(2)}/1M</span>
              <span>Out: ${model.outputPricePer1m?.toFixed(2)}/1M</span>
            </Box>
            <Box className="admin-model-card-actions">
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => handleOpenDialog(model)}
              >
                Edit
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => handleDeleteModel(model.modelId)}
              >
                Delete
              </Button>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Model Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth className="admin-dialog">
        <DialogTitle>{editMode ? 'Edit Model' : 'Add New Model'}</DialogTitle>
        <DialogContent>
          <Box className="admin-dialog-form">
            <TextField
              fullWidth
              label="Model ID"
              value={selectedModel?.modelId || ''}
              onChange={(e) => handleModelChange('modelId', e.target.value)}
              disabled={editMode}
              size="small"
              helperText={editMode ? "Cannot change after creation" : "Unique identifier"}
            />
            <TextField
              fullWidth
              label="Name"
              value={selectedModel?.modelName || ''}
              onChange={(e) => handleModelChange('modelName', e.target.value)}
              size="small"
            />
            <TextField
              fullWidth
              label="Description"
              value={selectedModel?.modelDescription || ''}
              onChange={(e) => handleModelChange('modelDescription', e.target.value)}
              multiline
              rows={2}
              size="small"
            />
            <TextField
              fullWidth
              label="Settings JSON"
              value={selectedModel?.settingsJson || ''}
              onChange={(e) => handleModelChange('settingsJson', e.target.value)}
              multiline
              rows={4}
              size="small"
            />
            <Box className="admin-form-row">
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedModel?.modelEnabled || false}
                    onChange={(e) => handleModelChange('modelEnabled', e.target.checked)}
                  />
                }
                label="Enabled"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedModel?.premium || false}
                    onChange={(e) => handleModelChange('premium', e.target.checked)}
                  />
                }
                label="Premium"
              />
            </Box>
            <Typography variant="caption" color="textSecondary">
              Pricing (per 1M tokens)
            </Typography>
            <Box className="admin-form-row">
              <TextField
                label="Input $"
                type="number"
                value={selectedModel?.inputPricePer1m || 0}
                onChange={(e) => handleModelChange('inputPricePer1m', parseFloat(e.target.value) || 0)}
                size="small"
                inputProps={{ step: 0.01, min: 0 }}
              />
              <TextField
                label="Cached $"
                type="number"
                value={selectedModel?.cachedInputPricePer1m || 0}
                onChange={(e) => handleModelChange('cachedInputPricePer1m', parseFloat(e.target.value) || 0)}
                size="small"
                inputProps={{ step: 0.01, min: 0 }}
              />
              <TextField
                label="Output $"
                type="number"
                value={selectedModel?.outputPricePer1m || 0}
                onChange={(e) => handleModelChange('outputPricePer1m', parseFloat(e.target.value) || 0)}
                size="small"
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveModel} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CopilotModelsTab;